/**
 * MUI v6 Theme with CSS variables enabled.
 *
 * `extendTheme` (v6 stable) emits theme values as `--ads-*` CSS custom
 * properties on the root, and `CssVarsProvider` swaps the active scheme
 * by toggling a data attribute on the root.
 *
 * Two color schemes: `dark` (default) and `light`. Both pull from the
 * SAME `tokens/semantic/color.json` source — each role token now carries
 * BOTH context values inside `$value: { dark, light }` (Path-2 layout
 * matching the Figma Semantics collection). Per scheme we build a ramp
 * that resolves each role to the appropriate context.
 *
 * Inverse-* fields on each palette role hold the OTHER context's value
 * (used by overlays — e.g. a light Modal inside a dark page reads
 * `palette.primary.inverseContainer` for its header tint). In light
 * scheme the same `inverseContainer` key returns the dark-context value
 * — symmetry preserved across schemes.
 *
 * Components read colours via `theme.palette.*` in styled callbacks —
 * Option B. The JSON tokens become a build-time source feeding palette;
 * no component reaches for the JSON directly. Non-colour tokens
 * (borders, sizing, motion, brand, spacing) are still consumed via
 * direct JSON import + the shared `v()` unwrapper in `src/tokens/util.ts`.
 *
 * Shadows: 25 nulls. The AdSensor system has no elevation layer.
 *
 * @format
 */

import {extendTheme} from '@mui/material/styles';
import type {Theme} from '@mui/material/styles';

import semanticColors from 'ui/tokens/semantic/color.json';
import datavizTokens from 'ui/tokens/semantic/dataviz.json';
import bordersTokens from 'ui/tokens/semantic/borders.json';
import motionTokens from 'ui/tokens/semantic/motion.json';
import {v, vLight} from 'ui/tokens/util';

import {muiTypography} from './typography';

// Side-effect import — module augmentation only.
import './paletteAugmentation';

const c = semanticColors.color;
const radius = bordersTokens.radius;

// Spacing base = 4 — единый с основным приложением (src/theme.tsx: spacing: 4).
// theme.spacing(1) = 4px, theme.spacing(4) = 16px.
const SPACING_BASE = 4;

// ── Role-ramp builder ───────────────────────────────────────────────────
//
// Every role group with the brand spectrum (primary, good, bad, alert)
// has the same 8 tokens. For each context we read the right `dark` or
// `light` side of each token's dual `$value`. The inverse-* palette
// fields point at the OTHER context — so they always represent the
// overlay scenario (a light surface inside a dark page, or vice versa).

type BrandTokens = {
  [k: string]: {$value: {dark: string; light: string} | string};
};

function brandRoleDark(g: BrandTokens, base: string) {
  return {
    main: v(g[base]),
    light: v(g[`hover-${base}`]),
    dark: v(g[`active-${base}`]),
    contrastText: v(g[`on-${base}`]),
    hover: v(g[`hover-${base}`]),
    active: v(g[`active-${base}`]),
    container: v(g[`${base}-container`]),
    onContainer: v(g[`on-${base}-container`]),
    hoverContainer: v(g[`hover-${base}-container`]),
    activeContainer: v(g[`active-${base}-container`]),
    // Inverse fields hold the OTHER context's values — used by overlays.
    inverseMain: vLight(g[base]),
    inverseContrastText: vLight(g[`on-${base}`]),
    inverseHover: vLight(g[`hover-${base}`]),
    inverseActive: vLight(g[`active-${base}`]),
    inverseContainer: vLight(g[`${base}-container`]),
    inverseOnContainer: vLight(g[`on-${base}-container`]),
    inverseHoverContainer: vLight(g[`hover-${base}-container`]),
    inverseActiveContainer: vLight(g[`active-${base}-container`]),
  };
}

function brandRoleLight(g: BrandTokens, base: string) {
  return {
    main: vLight(g[base]),
    light: vLight(g[`hover-${base}`]),
    dark: vLight(g[`active-${base}`]),
    contrastText: vLight(g[`on-${base}`]),
    hover: vLight(g[`hover-${base}`]),
    active: vLight(g[`active-${base}`]),
    container: vLight(g[`${base}-container`]),
    onContainer: vLight(g[`on-${base}-container`]),
    hoverContainer: vLight(g[`hover-${base}-container`]),
    activeContainer: vLight(g[`active-${base}-container`]),
    // Inverse fields hold the OTHER context — i.e. the dark column.
    inverseMain: v(g[base]),
    inverseContrastText: v(g[`on-${base}`]),
    inverseHover: v(g[`hover-${base}`]),
    inverseActive: v(g[`active-${base}`]),
    inverseContainer: v(g[`${base}-container`]),
    inverseOnContainer: v(g[`on-${base}-container`]),
    inverseHoverContainer: v(g[`hover-${base}-container`]),
    inverseActiveContainer: v(g[`active-${base}-container`]),
  };
}

const primaryRoleDark = brandRoleDark(c.primary as BrandTokens, 'primary');
const goodRoleDark = brandRoleDark(c.good as BrandTokens, 'good');
const badRoleDark = brandRoleDark(c.bad as BrandTokens, 'bad');
const alertRoleDark = brandRoleDark(c.alert as BrandTokens, 'alert');

const primaryRoleLight = brandRoleLight(c.primary as BrandTokens, 'primary');
const goodRoleLight = brandRoleLight(c.good as BrandTokens, 'good');
const badRoleLight = brandRoleLight(c.bad as BrandTokens, 'bad');
const alertRoleLight = brandRoleLight(c.alert as BrandTokens, 'alert');

// ── Surface ramps — per scheme ─────────────────────────────────────────
//
// Non-inverse keys = this scheme's context. Inverse keys = the OTHER
// context. `inverseSurface` and `inverseOnSurface` are standalone roles
// in the Figma table (not synonyms for "surface in the other context")
// — they're read via their own tokens which also carry dual values.

const surfaceRampDark = {
  surface1: v(c.surface['surface-1']),
  surface2: v(c.surface['surface-2']),
  surface3: v(c.surface['surface-3']),
  onSurface: v(c.surface['on-surface']),
  onSurfaceLight: v(c.surface['on-surface-light']),
  onSurfaceMuted: v(c.surface['on-surface-muted']),
  inverseSurface: v(c.surface['inverse-surface']),
  inverseSurface1: vLight(c.surface['surface-1']),
  inverseSurface2: vLight(c.surface['surface-2']),
  inverseSurface3: vLight(c.surface['surface-3']),
  inverseOnSurface: v(c.surface['inverse-on-surface']),
  inverseOnSurfaceLight: vLight(c.surface['on-surface-light']),
  inverseOnSurfaceMuted: vLight(c.surface['on-surface-muted']),
};

const surfaceRampLight = {
  surface1: vLight(c.surface['surface-1']),
  surface2: vLight(c.surface['surface-2']),
  surface3: vLight(c.surface['surface-3']),
  onSurface: vLight(c.surface['on-surface']),
  onSurfaceLight: vLight(c.surface['on-surface-light']),
  onSurfaceMuted: vLight(c.surface['on-surface-muted']),
  inverseSurface: vLight(c.surface['inverse-surface']),
  inverseSurface1: v(c.surface['surface-1']),
  inverseSurface2: v(c.surface['surface-2']),
  inverseSurface3: v(c.surface['surface-3']),
  inverseOnSurface: vLight(c.surface['inverse-on-surface']),
  inverseOnSurfaceLight: v(c.surface['on-surface-light']),
  inverseOnSurfaceMuted: v(c.surface['on-surface-muted']),
};

// ── Disabled ramps — per scheme ────────────────────────────────────────

const disabledRampDark = {
  main: v(c.disabled.disabled),
  low: v(c.disabled['disabled-low']),
  high: v(c.disabled['disabled-high']),
  inverseMain: vLight(c.disabled.disabled),
  inverseLow: vLight(c.disabled['disabled-low']),
  inverseHigh: vLight(c.disabled['disabled-high']),
};

const disabledRampLight = {
  main: vLight(c.disabled.disabled),
  low: vLight(c.disabled['disabled-low']),
  high: vLight(c.disabled['disabled-high']),
  inverseMain: v(c.disabled.disabled),
  inverseLow: v(c.disabled['disabled-low']),
  inverseHigh: v(c.disabled['disabled-high']),
};

// ── Chart series ramp — per scheme ─────────────────────────────────────
//
// Series colours are pure identity: they say WHICH series, never whether
// it is doing well. That is why they are a namespace of their own instead
// of a reach into good/bad/alert — a chart that paints its third line the
// same green as the "всё в порядке" badge has made a claim nobody wrote.
//
// Emitted as `--ads-palette-chart-seriesN`, which is what `chartVar()` in
// the Chart component reads. Going through the palette (rather than a
// direct JSON import) is what makes the dark↔light flip free: the
// `data-color-scheme` cascade owns the value, exactly as it does for
// `surface`.

const d = datavizTokens.dataviz;

const chartRampDark = {
  series1: v(d['series-1']),
  series2: v(d['series-2']),
  series3: v(d['series-3']),
  series4: v(d['series-4']),
  series5: v(d['series-5']),
  series6: v(d['series-6']),
  forecast: v(d.forecast),
};

const chartRampLight = {
  series1: vLight(d['series-1']),
  series2: vLight(d['series-2']),
  series3: vLight(d['series-3']),
  series4: vLight(d['series-4']),
  series5: vLight(d['series-5']),
  series6: vLight(d['series-6']),
  forecast: vLight(d.forecast),
};

// ── Theme ──────────────────────────────────────────────────────────────

export const theme = extendTheme({
  cssVarPrefix: 'ads',
  defaultColorScheme: 'dark',

  // CRITICAL: when both `colorSchemes.light` and `colorSchemes.dark` are
  // provided, MUI v6's `extendTheme` silently defaults
  // `colorSchemeSelector` to `'media'`, switching to OS-preference-driven
  // mode via `@media (prefers-color-scheme: …)`. In that mode the
  // cascade is owned by the browser's setting; `setMode` becomes a
  // no-op (MUI even logs a dev warning) and our Storybook toolbar
  // Context flip stops affecting anything.
  //
  // We pin to a data attribute so MUI emits `:root, [data-color-scheme="dark"]`
  // and `[data-color-scheme="light"]` rules instead. That puts the
  // switching back in our hands: CssVarsProvider sets the attribute on
  // documentElement, SurfaceContextProvider can override it on a
  // subtree, and the cascade does the rest.
  //
  // `'data-color-scheme'` matches `InitColorSchemeScript.DEFAULT_ATTRIBUTE`
  // — the same name used elsewhere across our setup (Storybook
  // decorator, SurfaceContextProvider wrapper).
  colorSchemeSelector: 'data-color-scheme',

  colorSchemes: {
    dark: {
      palette: {
        mode: 'dark',
        primary: primaryRoleDark,
        good: goodRoleDark,
        bad: badRoleDark,
        alert: alertRoleDark,

        // MUI built-in aliases mapped onto our roles so any third-party
        // component reading palette.error / palette.success / palette.warning
        // resolves to the correct AdSensor token. `secondary` and `info`
        // have no AdSensor counterpart and alias to `primary` (better than
        // leaving them as MUI defaults).
        secondary: primaryRoleDark,
        success: goodRoleDark,
        error: badRoleDark,
        warning: alertRoleDark,
        info: primaryRoleDark,

        surface: surfaceRampDark,
        disabledStates: disabledRampDark,
        chart: chartRampDark,

        background: {
          default: v(c.surface.background),
          paper: v(c.surface['surface-1']),
        },
        text: {
          primary: v(c.surface['on-surface']),
          secondary: v(c.surface['on-surface-light']),
          disabled: v(c.surface['on-surface-muted']),
        },
        divider: v(c.surface['surface-2']),
        action: {
          disabled: v(c.disabled.disabled),
          disabledBackground: v(c.disabled['disabled-low']),
        },
      },
    },

    light: {
      palette: {
        mode: 'light',
        primary: primaryRoleLight,
        good: goodRoleLight,
        bad: badRoleLight,
        alert: alertRoleLight,

        secondary: primaryRoleLight,
        success: goodRoleLight,
        error: badRoleLight,
        warning: alertRoleLight,
        info: primaryRoleLight,

        surface: surfaceRampLight,
        disabledStates: disabledRampLight,
        chart: chartRampLight,

        background: {
          default: vLight(c.surface.background),
          paper: vLight(c.surface['surface-1']),
        },
        text: {
          primary: vLight(c.surface['on-surface']),
          secondary: vLight(c.surface['on-surface-light']),
          disabled: vLight(c.surface['on-surface-muted']),
        },
        divider: vLight(c.surface['surface-2']),
        action: {
          disabled: vLight(c.disabled.disabled),
          disabledBackground: vLight(c.disabled['disabled-low']),
        },
      },
    },
  },

  shape: {
    borderRadius: parseInt(v(radius.m)),
  },

  spacing: SPACING_BASE,

  typography: muiTypography as unknown as Theme['typography'],

  transitions: {
    // Mapping rationale (we have 5 duration tokens; MUI exposes 7 named
    // durations). `slow` lands on `short` AND `enteringScreen` intentionally
    // — MUI's "entering" timing matches our "slow" contemplative motion;
    // `standard` falls between the two and is mapped to the same value as
    // `short` because we have no discrete in-between token. `instant` and
    // `loop` from the JSON intentionally do NOT participate — `instant=0`
    // is the absence of a transition, and `loop=1000` is the animation
    // period, not a transition envelope.
    duration: {
      shortest: parseInt(v(motionTokens.motion.duration.fast)),
      shorter: parseInt(v(motionTokens.motion.duration.base)),
      short: parseInt(v(motionTokens.motion.duration.slow)),
      standard: parseInt(v(motionTokens.motion.duration.slow)),
      complex: parseInt(v(motionTokens.motion.duration.slower)),
      enteringScreen: parseInt(v(motionTokens.motion.duration.slow)),
      leavingScreen: parseInt(v(motionTokens.motion.duration.base)),
    },
    easing: {
      easeInOut: v(motionTokens.motion.easing.standard),
      easeOut: v(motionTokens.motion.easing.decelerate),
      easeIn: v(motionTokens.motion.easing.accelerate),
      sharp: v(motionTokens.motion.easing.emphasized),
    },
  },

  shadows: Array(25).fill('none') as Theme['shadows'],

  components: {
    MuiPaper: {
      defaultProps: {elevation: 0},
    },
  },
});
